/**
 * Reading and writing a geofence ring, with no map attached.
 *
 * Note: separate from GeofenceEditor because GeofenceLayer needs the parser and nothing else. Importing
 * it from the editor dragged geoman's whole drawing toolbar and its stylesheet into the machine map,
 * which never draws anything.
 */

import { formatNumber } from "../../utilities/format";
import i18n from "../../i18n";

export type Vertex = { lat: number; lng: number };

export const MINIMUM_VERTICES = 3;

// Note: the stored form is GeoJSON, which is [longitude, latitude]. Leaflet is [latitude, longitude].
// Every transposition happens in these two functions and nowhere else - it is the single most common way
// a fence ends up in the sea off Somalia.
export function polygonToGeoJson(vertices: Vertex[]): string | null {
  if (vertices.length < MINIMUM_VERTICES) return null;

  const ring = vertices.map(v => [v.lng, v.lat]);
  ring.push([vertices[0].lng, vertices[0].lat]);

  return JSON.stringify({ type: "Polygon", coordinates: [ring] });
}

export function ringsMatch(a: Vertex[], b: Vertex[]): boolean {
  return a.length === b.length
    && a.every((v, i) => Math.abs(v.lat - b[i].lat) < 1e-9 && Math.abs(v.lng - b[i].lng) < 1e-9);
}

/**
 * Reads back whatever is in the paste box: GeoJSON, WKT, or one "latitude, longitude" per line.
 *
 * Note: it mirrors the server's GeoPolygon parser rather than being the authority. The server validates
 * again on save and is the one that decides - this exists so the map can draw what was pasted, and so a
 * typo is caught while the admin is still looking at it instead of after a round trip.
 */
export function parseGeofencePolygon(raw: string): { vertices: Vertex[]; error: string | null } {
  const text = raw.trim();
  if (!text) return { vertices: [], error: null };

  try {
    if (text.startsWith("{")) {
      const parsed = JSON.parse(text);
      const geometry = parsed.geometry ?? parsed;
      const ring = geometry?.coordinates?.[0];

      if (!Array.isArray(ring))
        return { vertices: [], error: i18n.t("geofencePolygon.noRing", { ns: "telemetry" }) };

      return closeOff(ring.map((p: number[]) => ({ lat: p[1], lng: p[0] })));
    }

    if (text.toUpperCase().startsWith("POLYGON")) {
      const inner = text.slice(text.indexOf("(") + 1, text.lastIndexOf(")")).replace(/[()]/g, "");
      return closeOff(inner.split(",").map(pair => {
        const [lng, lat] = pair.trim().split(/\s+/).map(Number);
        return { lat, lng };
      }));
    }

    return closeOff(text.split(/[\r\n;]+/).filter(Boolean).map(line => {
      const [lat, lng] = line.split(/[,\t ]+/).filter(Boolean).map(Number);
      return { lat, lng };
    }));
  } catch {
    return { vertices: [], error: i18n.t("geofencePolygon.unreadable", { ns: "telemetry" }) };
  }
}

// Note: flat-earth metres, not a geodesic library - the same approximation the FMS simulator's own
// polygon generators and size report already use, accurate to a fraction of a percent at a geofence's
// scale (tens to hundreds of metres). Longitude is scaled by the polygon's own mean latitude rather than
// a fixed anchor, since this runs against hand-drawn fences anywhere on the map, not just the synthetic mine.
const METRES_PER_DEG_LAT = 110574;

export function polygonAreaAndPerimeterMetres(vertices: Vertex[]): { areaSqM: number; perimeterM: number } | null {
  if (vertices.length < MINIMUM_VERTICES) return null;

  const meanLatRad = (vertices.reduce((sum, v) => sum + v.lat, 0) / vertices.length) * Math.PI / 180;
  const metresPerDegLon = METRES_PER_DEG_LAT * Math.cos(meanLatRad);

  const points = vertices.map(v => ({ x: v.lng * metresPerDegLon, y: v.lat * METRES_PER_DEG_LAT }));

  let shoelace = 0;
  let perimeterM = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    shoelace += a.x * b.y - b.x * a.y;
    perimeterM += Math.hypot(b.x - a.x, b.y - a.y);
  }

  return { areaSqM: Math.abs(shoelace) / 2, perimeterM };
}

// Note: mining land area is conventionally read in hectares, not m² - a pit runs to tens of thousands of
// m² (low single-digit hectares), where the raw figure is just noise to parse. km² only matters for a pit
// bigger than most whole mine sites, but the tier is here so a genuinely huge polygon still reads cleanly
// rather than as "1,234.56 ha". Below 1 ha (most blast patterns and dig blocks), m² stays the natural unit.
export function formatAreaMetres(areaSqM: number): string {
  if (areaSqM >= 1_000_000) return `${formatNumber(areaSqM / 1_000_000, { maximumFractionDigits: 2 })} km²`;
  if (areaSqM >= 10_000) return `${formatNumber(areaSqM / 10_000, { maximumFractionDigits: 2 })} ha`;
  return `${formatNumber(Math.round(areaSqM))} m²`;
}

function closeOff(vertices: Vertex[]): { vertices: Vertex[]; error: string | null } {
  const clean = vertices.filter(v => Number.isFinite(v.lat) && Number.isFinite(v.lng));

  if (clean.length !== vertices.length)
    return { vertices: [], error: i18n.t("geofencePolygon.notNumbers", { ns: "telemetry" }) };

  // Note: a repeated closing vertex is idiomatic in GeoJSON and WKT and would draw a zero-length edge.
  if (clean.length > 1
    && Math.abs(clean[0].lat - clean[clean.length - 1].lat) < 1e-9
    && Math.abs(clean[0].lng - clean[clean.length - 1].lng) < 1e-9)
    clean.pop();

  if (clean.length < MINIMUM_VERTICES)
    return { vertices: [], error: i18n.t("geofencePolygon.tooFewCorners", { ns: "telemetry", found: clean.length }) };

  const outOfRange = clean.find(v => Math.abs(v.lat) > 90 || Math.abs(v.lng) > 180);
  if (outOfRange)
    return {
      vertices: [],
      error: i18n.t("geofencePolygon.offGlobe", { ns: "telemetry", latitude: outOfRange.lat, longitude: outOfRange.lng }),
    };

  return { vertices: clean, error: null };
}
